/**
 * Small building blocks: a page, a card, a field, a button, honest loading and error states. Loading never reads as a
 * finding or an all-clear: a loading panel says it is loading, an empty one says what was counted, and a failed one
 * says so and offers to try again.
 *
 * PROVENANCE: C:\Olivia\web\src\components\ui.tsx @ f92475e, copied 2026-10-12 (Page, Card, Loading, retryable,
 * ErrorText, Show, LaterNote). CHANGES: Pascal's tokens (ink on linen, the amber-brown accent); no icon library yet, so
 * states are words; Button and Field written here in the same shape.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { useLanguage, useT } from '../i18n/LanguageProvider'
import { ApiError } from '../lib/api'
import { errorText } from '../lib/errors'
import type { Loaded as LoadState } from '../lib/useLoad'

/** A page. `wide` gives the board the whole width; it scrolls sideways inside its own area, never the page. */
export function Page({ title, subtitle, actions, children, wide }: { title: string; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode; wide?: boolean }) {
  return (
    <div className={`mx-auto w-full px-4 py-6 sm:px-6 ${wide ? 'max-w-[110rem]' : 'max-w-5xl'}`}>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight break-words text-ink">{title}</h1>
          {subtitle && <div className="mt-1 text-muted">{subtitle}</div>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  )
}

export function Card({ title, children, aside }: { title?: ReactNode; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="min-w-0 rounded-lg border border-line bg-card p-4 shadow-sm sm:p-5">
      {(title || aside) && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {title && <h2 className="text-lg font-semibold text-ink">{title}</h2>}
          {aside}
        </div>
      )}
      {children}
    </section>
  )
}

export const inputClass = 'w-full rounded-md border border-line bg-card px-3 py-2 text-ink'

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="block font-medium text-ink">{label}</span>
      {children}
      {hint && <span className="block text-sm text-muted">{hint}</span>}
    </label>
  )
}

export function Button({ children, variant = 'primary', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'quiet' }) {
  const look =
    variant === 'primary'
      ? 'bg-accent text-accent-ink hover:bg-accent-strong'
      : 'border border-line bg-card text-ink hover:bg-surface'
  return (
    <button type="button" {...rest} className={`rounded-md px-4 py-2 font-medium disabled:opacity-60 ${look} ${rest.className ?? ''}`}>
      {children}
    </button>
  )
}

export function Loading() {
  const t = useT()
  return (
    <p className="text-muted" role="status">
      {t('common.loading')}
    </p>
  )
}

/** Whether asking again could change the answer: not for a refusal (a 4xx), which says the same the second time. */
export function retryable(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status >= 500 || error.status === 408 || error.status === 429
}

/** An error as a person reads it, in the interface language. A refusal offers no "try again". */
export function ErrorText({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useLanguage()
  return (
    <div className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-danger" role="alert">
      <p>{errorText(error, t)}</p>
      {onRetry && retryable(error) && (
        <button type="button" onClick={onRetry} className="mt-1 text-sm underline">
          {t('common.retry')}
        </button>
      )}
    </div>
  )
}

/** The three honest states of a read: loading, failed (with a retry), or the data. */
export function Show<T>({ load, children }: { load: LoadState<T>; children: (data: T) => ReactNode }) {
  if (load.error) return <ErrorText error={load.error} onRetry={load.reload} />
  if (load.data === null) return <Loading />
  return <>{children(load.data)}</>
}

/** A section that is not built yet: says so in words and shows nothing invented. */
export function LaterNote({ text }: { text: string }) {
  const t = useT()
  return (
    <div className="rounded-md border border-dashed border-line bg-surface px-3 py-2 text-muted">
      <span className="mr-2 rounded bg-accent-soft px-1.5 py-0.5 text-sm font-medium text-ink">{t('common.notYet')}</span>
      {text}
    </div>
  )
}
